import { Injectable, OnDestroy } from '@angular/core';

import { Subject, take, takeUntil } from 'rxjs';

import { MwCompanyService, MwAuthService } from '@core/auth';
import { ProfileMenuItem } from '@core/config';
import { MwAppConfigService } from '@core/services';

import { ProfileMenuEventsService } from './profile-menu-events.service';

@Injectable()
export class ProfileMenuBuilderService implements OnDestroy {
  static nextId = 0;
  readonly _id = ProfileMenuBuilderService.nextId++;
  private readonly DefaultUserProfileMenuItems: ProfileMenuItem[] = [
    {
      title: 'CORE.PROFILE.MENU.my_profile',
      icon: 'person',
      action: () => this.eventsService.showMyProfile(),
      orderRank: 0,
    },
    {
      title: 'CORE.PROFILE.MENU.log_off',
      icon: 'logout',
      action: () => this.eventsService.requestLogout(),
      orderRank: 100,
    },
  ];

  private readonly CompanyChangeMenuItem: ProfileMenuItem = {
    title: 'CORE.PROFILE.MENU.change_company',
    icon: 'apartment',
    action: () => this.eventsService.requestCompanyChange(),
    orderRank: 1,
  };

  private unsubscribeAll$ = new Subject<void>();

  constructor(
    private companyService: MwCompanyService,
    private readonly authService: MwAuthService,
    private readonly configService: MwAppConfigService,
    private readonly eventsService: ProfileMenuEventsService
  ) {}

  setup(): void {
    this.subscribeToCompaniesCountChange();
    this.subscribeToUserLogin();
  }

  setMenuItem(menuItem: ProfileMenuItem): void {
    this.configService
      .getConfig()
      .pipe(take(1))
      .subscribe((config) => {
        const allMenuItems = this.getCurrentMenuItems().filter(
          (item) => item.title !== menuItem.title
        );

        allMenuItems.push(menuItem);

        this.configService.setConfig({
          profileMenuItems: allMenuItems,
        });
      });
  }

  removeMenuItem(menuItem: ProfileMenuItem): void {
    this.configService
      .getConfig()
      .pipe(take(1))
      .subscribe((config) => {
        const allMenuItems = this.getCurrentMenuItems().filter(
          (item) => item.title !== menuItem.title
        );

        this.configService.setConfig({
          profileMenuItems: allMenuItems,
        });
      });
  }

  clearMenuItems(): void {
    this.configService.setConfigChunk('profileMenuItems', []);
  }

  ngOnDestroy(): void {
    this.unsubscribeAll$.next();
    this.unsubscribeAll$.complete();
  }

  private onCompaniesCountChange(hasManyCompanies: boolean): void {
    if (hasManyCompanies) {
      this.setMenuItems([this.CompanyChangeMenuItem]);
    } else {
      this.removeMenuItem(this.CompanyChangeMenuItem);
    }
  }

  private subscribeToCompaniesCountChange(): void {
    this.companyService.hasManyCompanies$
      .pipe(takeUntil(this.unsubscribeAll$))
      .subscribe((value) => this.onCompaniesCountChange(value));
  }

  private subscribeToUserLogin(): void {
    this.authService.loggedIn$
      .pipe(takeUntil(this.unsubscribeAll$))
      .subscribe((isLoggedIn) => {
        if (isLoggedIn) {
          const isChangeCompanyMenuItemExist = this.getCurrentMenuItems().find(
            (t) => t.title === this.CompanyChangeMenuItem.title
          );

          const menuItems = [...this.DefaultUserProfileMenuItems];

          if (isChangeCompanyMenuItemExist) {
            menuItems.push(this.CompanyChangeMenuItem);
          }

          this.setMenuItems(menuItems);
        } else {
          this.clearMenuItems();
        }
      });
  }

  private getCurrentMenuItems(): ProfileMenuItem[] {
    return [...(this.configService.getInstantConfig().profileMenuItems || [])];
  }

  private setMenuItems(menuItems: ProfileMenuItem[]): void {
    let allMenuItems = this.getCurrentMenuItems();

    menuItems.forEach((item) => {
      allMenuItems = allMenuItems.filter((t) => t.title !== item.title);
      allMenuItems.push(item);
    });

    this.configService.setConfig({
      profileMenuItems: allMenuItems,
    });
  }
}
